import { FC, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import TariffBreakdownPopover from "@/pages/COMMON/product-catalogue/components/tariff-breakdown-popover";
import type {
  AdditionalTariff,
  ProductBreakdownLine,
  LineType,
} from "@/services/product-catalogue";
import {
  CheckCircleOutline,
  OpenInNew,
  PlayArrow,
} from "@mui/icons-material";

import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import {
  useGetTariffEngineeringProductQuery,
  useRunTariffEngineeringResearchForProductMutation,
} from "@/services/tariff-engineering";
import ProductContextPanel from "@/components/product-context-panel";
import type {
  TariffEngineeringOpportunity,
  TariffEngineeringStrategy,
  TariffEngineeringConfidence,
} from "@/types/tariff-engineering";

const strategyColor = (s: TariffEngineeringStrategy): string => {
  switch (s) {
    case "exclusion":
      return "#2e7d32";
    case "reclassify":
      return "#1565c0";
    case "substantial_transformation":
      return "#6a1b9a";
    case "conditional_ch99_exclusion":
      return "#c79100";
    case "metal_breakdown":
      return "#ef6c00";
    case "other":
      return "#616161";
  }
};

const strategyLabel = (s: TariffEngineeringStrategy): string => {
  switch (s) {
    case "reclassify":
      return "Reclassify";
    case "substantial_transformation":
      return "Substantial Transformation";
    case "conditional_ch99_exclusion":
      return "Conditional Ch99 Exclusion";
    case "exclusion":
      return "Exclusion List";
    case "metal_breakdown":
      return "Metal Breakdown";
    case "other":
      return "Other";
  }
};

const confidenceColor = (
  c: TariffEngineeringConfidence
): "default" | "info" | "warning" | "success" => {
  switch (c) {
    case "high":
      return "success";
    case "medium":
      return "info";
    case "low":
      return "warning";
  }
};

const fmtUsd = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const OpportunityRow: FC<{ o: TariffEngineeringOpportunity }> = ({ o }) => (
  <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ mb: 1.5, flexWrap: "wrap" }}
    >
      <Chip
        label={strategyLabel(o.strategy)}
        size="small"
        sx={{
          backgroundColor: strategyColor(o.strategy),
          color: "white",
          fontWeight: 600,
        }}
      />
      <Chip
        label={`${o.confidence.toUpperCase()} confidence`}
        size="small"
        color={confidenceColor(o.confidence)}
      />
      <Typography variant="body2" sx={{ ml: "auto !important" }}>
        <Box component="span" sx={{ color: "text.secondary" }}>
          {o.currentRate}% →
        </Box>{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {o.projectedRate}%
        </Box>{" "}
        <Box component="span" sx={{ color: "success.main", fontWeight: 600 }}>
          (−{o.savingsPct.toFixed(1)}pp)
        </Box>
      </Typography>
    </Stack>

    {(o.altHts || o.altOrigin || o.altCh99Code) && (
      <Stack direction="row" spacing={3} sx={{ mb: 1.5 }}>
        {o.altHts && (
          <Typography variant="body2">
            <strong>Alt HTS:</strong>{" "}
            <Box component="span" fontFamily="monospace">
              {o.altHts}
            </Box>
          </Typography>
        )}
        {o.altOrigin && (
          <Typography variant="body2">
            <strong>Alt Origin:</strong> {o.altOrigin}
          </Typography>
        )}
        {o.altCh99Code && (
          <Typography variant="body2">
            <strong>Alt Ch99:</strong>{" "}
            <Box component="span" fontFamily="monospace">
              {o.altCh99Code}
            </Box>
          </Typography>
        )}
      </Stack>
    )}

    <Typography variant="body2" sx={{ mb: 1.5 }}>
      {o.rationale}
    </Typography>

    {o.conditionQuote && (
      <Box
        sx={{
          borderLeft: 3,
          borderColor: strategyColor(o.strategy),
          pl: 1.5,
          my: 1.5,
          fontStyle: "italic",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          “{o.conditionQuote}”
        </Typography>
      </Box>
    )}

    {o.supportingRulings.length > 0 && (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Supporting rulings:
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
          {o.supportingRulings.map((r) => (
            <Chip
              key={r.id}
              label={r.id}
              size="small"
              variant="outlined"
              icon={<OpenInNew sx={{ fontSize: 14 }} />}
              onClick={() => window.open(r.url, "_blank", "noopener,noreferrer")}
              clickable
            />
          ))}
        </Stack>
      </Box>
    )}

    {o.tradeDataEvidence && (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Trade-data evidence: {o.tradeDataEvidence.country}{" "}
        {(o.tradeDataEvidence.percentShare * 100).toFixed(1)}% share /{" "}
        {fmtUsd(o.tradeDataEvidence.totalValue)} annual
      </Typography>
    )}

    {o.instructions.length > 0 && (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          ACTION ITEMS
        </Typography>
        <List dense disablePadding>
          {o.instructions.map((s, i) => (
            <ListItem key={i} disablePadding sx={{ alignItems: "flex-start" }}>
              <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                <CheckCircleOutline
                  fontSize="small"
                  sx={{ color: strategyColor(o.strategy) }}
                />
              </ListItemIcon>
              <ListItemText
                primary={s}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    )}
  </Paper>
);

const TariffEngineeringProductDetail: FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { user } = useAppSelector((s) => s.auth);
  const { selectedClient } = useAppSelector((s) => s.client);
  const { selectedTenant } = useAppSelector((s) => s.tenantManagement);

  const scopeArgs = useMemo(() => {
    const args: { tenantId?: string; clientId?: string } = {};
    const isAdmin = user?.userRole === roles.TDADMIN;
    const isTDAdminTCD =
      isAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
    if (selectedTenant?._id) args.tenantId = selectedTenant._id;
    if (selectedClient?._id) {
      args.clientId = selectedClient._id;
    } else if (isTDAdminTCD && selectedTenant?._id) {
      args.clientId = selectedTenant._id;
    }
    return args;
  }, [user, selectedTenant?._id, selectedTenant?.tenantType, selectedClient?._id]);

  const { data, isFetching, refetch } = useGetTariffEngineeringProductQuery(
    { productId: productId!, ...scopeArgs },
    { skip: !productId }
  );

  // Poll while an analysis is mid-flight so the user sees the status flip
  // live without manual refresh. Stops once the run settles.
  const pendingAnalysis =
    data?.latestAnalysis?.status === "QUEUED" ||
    data?.latestAnalysis?.status === "RUNNING" ||
    data?.analysisStatus === "QUEUED" ||
    data?.analysisStatus === "RUNNING";
  useGetTariffEngineeringProductQuery(
    { productId: productId!, ...scopeArgs },
    { skip: !productId || !pendingAnalysis, pollingInterval: 5000 }
  );
  const [runResearch, { isLoading: isRunning }] =
    useRunTariffEngineeringResearchForProductMutation();
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  if (isFetching && !data) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Button onClick={() => navigate("/compliance/tariff-engineering")}>
          ← Back
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          Product not found.
        </Alert>
      </Box>
    );
  }

  const analysis = data.latestAnalysis;
  const handleRerun = async () => {
    await runResearch({ productId: data._id, ...scopeArgs }).unwrap();
    refetch();
  };

  return (
    <Box sx={{ p: 3, overflow: "auto", height: "100%" }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button
          onClick={() => navigate("/compliance/tariff-engineering")}
          size="small"
        >
          ← All products
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={
            isRunning ? <CircularProgress size={16} /> : <PlayArrow />
          }
          disabled={isRunning}
          onClick={handleRerun}
        >
          {analysis ? "Re-run research" : "Run research"}
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={4} alignItems="baseline" flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary">
              SKU
            </Typography>
            <Typography variant="h6" fontFamily="monospace">
              {data.sku}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary">
              Name
            </Typography>
            <Typography variant="h6">{data.name}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              HTS / Origin
            </Typography>
            <Typography variant="h6" fontFamily="monospace">
              {data.htsCode} / {data.countryOfOrigin}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Today's duty
            </Typography>
            <Typography
              variant="h5"
              onClick={() => setBreakdownOpen(true)}
              sx={{
                cursor: "pointer",
                textDecoration: "underline dotted",
                textUnderlineOffset: 4,
                "&:hover": { color: "primary.main" },
              }}
            >
              {((data.totalDutyRate ?? 0) * 100).toFixed(2)}%
            </Typography>
          </Box>
        </Stack>

        {data.description && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Description
            </Typography>
            <Typography variant="body2">{data.description}</Typography>
          </Box>
        )}

        {data.metalBreakdown &&
          (data.metalBreakdown.steel ||
            data.metalBreakdown.aluminum ||
            data.metalBreakdown.copper) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Metal breakdown
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                {data.metalBreakdown.steel ? (
                  <Chip
                    size="small"
                    label={`Steel ${data.metalBreakdown.steel}%`}
                  />
                ) : null}
                {data.metalBreakdown.aluminum ? (
                  <Chip
                    size="small"
                    label={`Aluminum ${data.metalBreakdown.aluminum}%`}
                  />
                ) : null}
                {data.metalBreakdown.copper ? (
                  <Chip
                    size="small"
                    label={`Copper ${data.metalBreakdown.copper}%`}
                  />
                ) : null}
              </Stack>
            </Box>
          )}

      </Paper>

      <TariffBreakdownPopover
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        productName={data.name}
        productSku={data.sku}
        htsCode={data.htsCode}
        countryOfOrigin={data.countryOfOrigin}
        breakdownLines={
          (data.breakdownLines || []).map((l) => ({
            ...l,
            lineType: l.lineType as LineType,
            applicableRules: l.applicableRules as AdditionalTariff[],
          })) as ProductBreakdownLine[]
        }
        additionalTariffs={(data.additionalTariffs as AdditionalTariff[]) || []}
        totalRate={data.totalDutyRate ?? 0}
        metalPercentage={data.metalPercentage ?? undefined}
      />

      <Box sx={{ mb: 2 }}>
        <ProductContextPanel productId={data._id} />
      </Box>

      {!analysis && (
        <Alert severity="info">
          No research run yet. Click "Run research" to generate opportunities.
        </Alert>
      )}

      {analysis && analysis.status === "QUEUED" && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            Queued for processing
          </Typography>
          <Typography variant="body2">
            Run will start shortly. This page polls every 5s — you'll see the
            opportunities here when the agent finishes.
          </Typography>
        </Alert>
      )}

      {analysis && analysis.status === "RUNNING" && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            Analysis in progress
          </Typography>
          <Typography variant="body2">
            Gemini is researching {data.htsCode} / {data.countryOfOrigin}{" "}
            using your linked chats + uploaded files. Typically 30–60s.
          </Typography>
        </Alert>
      )}

      {analysis && analysis.status === "FAILED" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            Research failed
          </Typography>
          <Typography variant="body2">
            {analysis.error?.message || "Unknown error"}
          </Typography>
          {analysis.error?.rawOutput && (
            <Box sx={{ mt: 1, maxHeight: 200, overflow: "auto" }}>
              <Typography
                component="pre"
                variant="caption"
                sx={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "monospace",
                  m: 0,
                }}
              >
                {analysis.error.rawOutput.slice(0, 1500)}
              </Typography>
            </Box>
          )}
        </Alert>
      )}

      {analysis && analysis.status === "DONE" && (
        <>
          <Box sx={{ mb: 2 }}>
            <Stack
              direction="row"
              alignItems="baseline"
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">
                Opportunities ({analysis.opportunities.length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                · finished{" "}
                {analysis.finishedAt
                  ? new Date(analysis.finishedAt).toLocaleString()
                  : ""}
              </Typography>
            </Stack>
            {analysis.opportunities.length === 0 ? (
              <Alert severity="success">
                <Typography variant="body2" fontWeight={600}>
                  No defensible tariff engineering applies
                </Typography>
                <Typography variant="body2">
                  Riffs Intelligence found no opportunity that clears
                  the evidence bar (verified CBP precedent, real Census
                  trade-flow data, or a Ch99 condition the SKU
                  satisfies). The agent's reasoning is in the analyst
                  notes below — including any ideas it considered and
                  rejected. Re-run after adding context (spec sheets,
                  linked chats) if you have signal the agent didn't see.
                </Typography>
              </Alert>
            ) : (
              [...analysis.opportunities]
                .sort((a, b) => b.savingsPct - a.savingsPct)
                .map((o, i) => <OpportunityRow key={i} o={o} />)
            )}
          </Box>

          {analysis.analystNotes && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Analyst Notes
              </Typography>
              <Box
                component="pre"
                sx={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                  fontSize: "0.9rem",
                  m: 0,
                }}
              >
                {analysis.analystNotes}
              </Box>
            </Paper>
          )}

          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="caption" color="text.secondary">
              Run metadata
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mt: 0.5, flexWrap: "wrap" }}>
              <Typography variant="body2">
                Model: <code>{analysis.modelVersion}</code>
              </Typography>
              <Typography variant="body2">
                Duration: {analysis.durationMs}ms
              </Typography>
              <Typography variant="body2">
                Tool calls: {analysis.toolCallLog.length}
              </Typography>
            </Stack>
            {analysis.toolCallLog.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Divider sx={{ mb: 1 }} />
                {analysis.toolCallLog.map((t, i) => (
                  <Typography
                    key={i}
                    variant="caption"
                    sx={{ display: "block", fontFamily: "monospace" }}
                    color={t.ok ? "text.secondary" : "error.main"}
                  >
                    {i + 1}. {t.toolName} {t.durationMs}ms — {t.summary.slice(0, 200)}
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default TariffEngineeringProductDetail;
